/* ─────────────────────────────────────────────────────────────────────────────
   GrowthOS migration — shared design tokens + primitives.

   These values are NOT guessed from screenshots — they were read directly off
   the live app (apps.prozo.com/growthos) via getComputedStyle on its real DOM:
   card gradient/border/shadow, KPI label/value typography (11px uppercase
   label w/ 0.14em tracking, 30px/700 value), the 32x32 rounded-xl icon chip
   (bg = metric color at ~10% alpha, icon = solid metric color), the funnel
   bar's clip-path trapezoid, and the page's Inter/#0B1220-on-#F6F7FB palette.
   Every GrowthOS page should import from here instead of redefining its own
   near-miss constants, so the whole module stays visually consistent and a
   future palette tweak happens in one place. */
import React, { useState } from 'react'
import { Box, Paper, Typography } from '@mui/material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

export const GX_FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
export const GX_BG = '#f6f7fb'
export const GX_BORDER = '#e6e9f2'
export const GX_INK_900 = '#0b1220'
export const GX_INK_700 = '#334155'
export const GX_INK_500 = '#64748b'
export const GX_POSITIVE = '#059669'
export const GX_NEGATIVE = '#e11d48'

// Metric color palette — matches the real app's per-KPI icon colors exactly
// (Boxes=indigo for shipments, IndianRupee=teal for revenue, Store=amber for
// brands, TrendingUp=pink for pipeline).
export const GX_COLORS = {
  indigo: '#4f46e5', cyan: '#06b6d4', teal: '#0d9488', amber: '#d97706',
  pink: '#e11d48', purple: '#7c3aed', sky: '#0284c7', lime: '#65a30d',
}
export const GX_CHART_PALETTE = [GX_COLORS.indigo, GX_COLORS.cyan, GX_COLORS.teal, GX_COLORS.amber, GX_COLORS.pink, GX_COLORS.purple, GX_COLORS.sky, GX_COLORS.lime]

export const GX_CARD_SX = {
  borderRadius: '16px', borderColor: GX_BORDER,
  background: 'linear-gradient(160deg, #ffffff 0%, #fbfbfe 100%)',
  boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)',
}

export const GX_PAGE_SX = { fontFamily: GX_FONT, color: GX_INK_900 }

export const gxFmtMoney = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`
// For a period-over-period CHANGE (can be + or -) — e.g. "revenue is up 12% vs last month".
export const gxFmtPct = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`)
// For an absolute RATE/ratio (always reads as "X% of something", never a delta) — e.g. a
// conversion rate or acceptance rate. No sign: "18.4%", never "+18.4%".
export const gxFmtRate = (v) => (v == null ? '—' : `${v}%`)
export const gxPctColor = (v) => (v == null ? GX_INK_500 : v >= 0 ? GX_POSITIVE : GX_NEGATIVE)

export function GxIconChip({ icon, color = GX_COLORS.indigo, size = 32 }) {
  return (
    <Box sx={{
      width: size, height: size, borderRadius: '12px', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: `${color}1a`, color,
    }}>
      {icon}
    </Box>
  )
}

export function GxKpiCard({ label, value, sub, subColor, icon, iconColor = GX_COLORS.indigo, onClick, active }) {
  return (
    <Paper
      variant="outlined" onClick={onClick}
      sx={{
        ...GX_CARD_SX, p: 2.5, flex: 1, minWidth: 200,
        cursor: onClick ? 'pointer' : 'default',
        boxShadow: active ? `0 0 0 2px ${iconColor}, ${GX_CARD_SX.boxShadow}` : GX_CARD_SX.boxShadow,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{
          fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em',
          color: GX_INK_500, lineHeight: 1.4,
        }}>{label}</Typography>
        {icon && <GxIconChip icon={icon} color={iconColor} />}
      </Box>
      <Typography sx={{ fontSize: 30, fontWeight: 700, color: GX_INK_900, mt: 1, lineHeight: 1.1 }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: 12, color: subColor || GX_INK_500, mt: 0.75 }}>{sub}</Typography>}
    </Paper>
  )
}

export function GxPill({ children, color = GX_COLORS.indigo, icon }) {
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.5, borderRadius: '9999px',
      bgcolor: `${color}14`, color, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {icon}
      {children}
    </Box>
  )
}

// Latest-successful-sync pills, reading real ingest-run data (useListIngestRunsQuery) — the
// caller owns the hook call and passes `runs`; this just renders whichever connectors this
// migration actually has (only real connectorKeys ever show — no "ABM Sheet"/ad-platform pill
// appears until a real connector for that source exists, matching the disclosed data-source gaps
// on the Marketing pages).
const GX_CONNECTOR_LABELS = { proship_api: 'ProShip', hubspot: 'HubSpot Activity', hubspot_api: 'HubSpot Activity' }
export function GxSyncStatusBar({ runs, labels = GX_CONNECTOR_LABELS }) {
  const latestByConnector = new Map()
  for (const r of runs || []) {
    if (r.status !== 'SUCCESS' || !r.finishedAt) continue
    const cur = latestByConnector.get(r.connectorKey)
    if (!cur || r.finishedAt > cur.finishedAt) latestByConnector.set(r.connectorKey, r)
  }
  const pills = Array.from(latestByConnector.entries())
    .filter(([key]) => labels[key])
    .map(([key, r]) => ({ key, label: labels[key], at: new Date(r.finishedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) }))
  if (!pills.length) return null
  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
      {pills.map((p) => (
        <GxPill key={p.key} color={GX_COLORS.teal}>
          {p.label} synced: {p.at}
        </GxPill>
      ))}
    </Box>
  )
}

export function GxDateBadge({ children }) {
  return (
    <GxPill color={GX_COLORS.indigo} icon={<CalendarTodayIcon sx={{ fontSize: 12 }} />}>{children}</GxPill>
  )
}

export function GxSectionCard({ title, icon, iconColor = GX_COLORS.indigo, badge, right, children }) {
  return (
    <Paper variant="outlined" sx={{ ...GX_CARD_SX, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2.5, pt: 2.5, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon && <Box sx={{ color: iconColor, display: 'flex', fontSize: 18 }}>{icon}</Box>}
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: GX_INK_700, letterSpacing: '0.02em' }}>{title}</Typography>
        </Box>
        {badge ? <GxDateBadge>{badge}</GxDateBadge> : right}
      </Box>
      <Box sx={{ px: 2.5, pb: 2.5 }}>{children}</Box>
    </Paper>
  )
}

// Trapezoid funnel stage — real app's exact clip-path (each bar tapers ~4%
// inward at the top edge relative to the bottom), gradient fill, white text.
// `width` is the bar's own visual taper (100 for the first/widest stage, narrower and
// centered for each stage after) — matches the real app's funnel silhouette. It's
// deliberately NOT derived from `pct` (the real percentage-of-first-stage, shown as text):
// a literally-proportional bar would go illegibly thin once a stage's real share drops to
// single digits, which is exactly the bug this replaces — every bar rendered at the same
// full width regardless of `pct`, since `pct` was only ever used for the displayed text.
export function GxFunnelStage({ icon, label, value, pct, gradient, width = 100 }) {
  return (
    <Box sx={{
      clipPath: 'polygon(4% 0%, 96% 0%, 100% 100%, 0% 100%)',
      background: gradient, color: '#fff', height: 56, mb: 0.75,
      width: `${width}%`, mx: 'auto',
    }}>
      {/* CSS resolves a box's own padding percentages against its CONTAINING block's width
          (this Box's parent, i.e. the full-width funnel column) — not this Box's own `width`.
          So on a narrow stage (e.g. width=21 for "Replied"), a `pl: '7%'` set directly above
          would be 7% of the full column, which is a third of this box's actual width, and the
          icon/label/value collide. Wrapping in an inner Box at width:'100%' makes ITS containing
          block this (already-narrowed) outer Box, so percentage padding here really is relative
          to this stage's own width — clearing the clip-path's diagonal on every stage width. */}
      <Box sx={{
        width: '100%', height: '100%', boxSizing: 'border-box',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        pl: '7%', pr: '9%',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          {icon}
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{label}</Typography>
        </Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700, flexShrink: 0 }}>
          {value} <Typography component="span" sx={{ fontSize: 12, fontWeight: 500, opacity: 0.85 }}>({pct}%)</Typography>
        </Typography>
      </Box>
    </Box>
  )
}

export function GxShareBar({ label, pct, width = 130 }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.25 }}>
      <Typography sx={{ fontSize: 13, width, flexShrink: 0, color: GX_INK_700 }}>{label}</Typography>
      <Box sx={{ flex: 1, height: 8, bgcolor: GX_BORDER, borderRadius: '9999px', overflow: 'hidden' }}>
        <Box sx={{
          height: '100%', width: `${Math.max(pct || 0, 2)}%`, borderRadius: '9999px',
          background: `linear-gradient(90deg, ${GX_COLORS.indigo}, ${GX_COLORS.cyan})`,
        }} />
      </Box>
      <Typography sx={{ fontSize: 14, fontWeight: 700, width: 52, textAlign: 'right', color: GX_INK_900 }}>{pct != null ? `${pct}%` : '—'}</Typography>
    </Box>
  )
}

export function GxRiskBadge({ label, color }) {
  return (
    <Box component="span" sx={{
      display: 'inline-block', px: 1.5, py: 0.25, borderRadius: '9999px', border: `1px solid ${color}`,
      color, fontSize: 12, fontWeight: 700, bgcolor: '#fff',
    }}>{label}</Box>
  )
}

// "View brands ↓" / "Hide detail ↑" — the real app's exact toggle copy, used on every
// drill-down tile (risk, cohort, POC performance) so a list of tiles doesn't dump a full
// table under each one by default.
export function GxDetailToggle({ open, onToggle, color, openLabel = 'View brands', closeLabel = 'Hide detail' }) {
  return (
    <Box
      component="button" onClick={onToggle}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.25, border: 'none', bgcolor: 'transparent',
        color, fontSize: 12, fontWeight: 600, cursor: 'pointer', p: 0, mt: 1,
      }}
    >
      {open ? closeLabel : openLabel}
      {open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
    </Box>
  )
}

export function GxRiskTile({ label, value, description, color, children }) {
  const [open, setOpen] = useState(false)
  return (
    <Paper variant="outlined" sx={{ ...GX_CARD_SX, p: 0, flex: 1, minWidth: 280, overflow: 'hidden' }}>
      <Box sx={{ height: 3, bgcolor: color }} />
      <Box sx={{ p: 2.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: GX_INK_500 }}>{label}</Typography>
        <Typography sx={{ fontSize: 30, fontWeight: 700, color, mt: 0.5, lineHeight: 1.1 }}>{value}</Typography>
        {description && <Typography sx={{ fontSize: 12, color: GX_INK_500, mt: 0.75 }}>{description}</Typography>}
        {children && <GxDetailToggle open={open} onToggle={() => setOpen((o) => !o)} color={color} />}
        {children && open && <Box sx={{ mt: 1.5 }}>{children}</Box>}
      </Box>
    </Paper>
  )
}

export function GxCohortTile({ label, value, sub, pct, color, children }) {
  const [open, setOpen] = useState(false)
  return (
    <Paper variant="outlined" sx={{ p: 2.5, flex: 1, minWidth: 220, borderColor: GX_BORDER, borderRadius: '14px' }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: GX_INK_700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 700, color: GX_INK_900, mt: 0.75 }}>{value}</Typography>
      <Typography sx={{ fontSize: 12, color: GX_INK_500, mt: 0.25 }}>{sub}</Typography>
      <Box sx={{ height: 6, bgcolor: GX_BORDER, borderRadius: '9999px', overflow: 'hidden', mt: 1.25 }}>
        <Box sx={{ height: '100%', width: `${Math.max(pct || 0, 2)}%`, bgcolor: color, borderRadius: '9999px' }} />
      </Box>
      <Typography sx={{ fontSize: 11, color: GX_INK_500, mt: 0.5 }}>{pct}% revenue share</Typography>
      {children && <GxDetailToggle open={open} onToggle={() => setOpen((o) => !o)} color={color} />}
      {children && open && <Box sx={{ mt: 1.5 }}>{children}</Box>}
    </Paper>
  )
}

// Top 5/10/20 concentration — three equal side-by-side columns (label, big %, bar
// underneath), matching the real app's layout (not stacked label-bar-pct rows).
export function GxConcentrationRow({ items }) {
  return (
    <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {items.map(({ label, pct }) => (
        <Box key={label} sx={{ flex: 1, minWidth: 180 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography sx={{ fontSize: 13, color: GX_INK_700 }}>{label}</Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: GX_COLORS.indigo }}>{pct != null ? `${pct}%` : '—'}</Typography>
          </Box>
          <Box sx={{ height: 8, bgcolor: GX_BORDER, borderRadius: '9999px', overflow: 'hidden' }}>
            <Box sx={{
              height: '100%', width: `${Math.max(pct || 0, 2)}%`, borderRadius: '9999px',
              background: `linear-gradient(90deg, ${GX_COLORS.indigo}, ${GX_COLORS.cyan})`,
            }} />
          </Box>
        </Box>
      ))}
    </Box>
  )
}

// Full-width collapsed-by-default metric row — icon chip + title + count + source-tag
// pills + one-line description, colored top accent, chevron. Matches the real app's
// Decision/Insights layout: every metric is a collapsed accordion row grouped under a
// category header, not a grid of always-visible cards.
export function GxAccordionRow({ icon, iconColor = GX_COLORS.indigo, title, count, tags, description, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Paper variant="outlined" sx={{ ...GX_CARD_SX, mb: 1.5, overflow: 'hidden' }}>
      <Box sx={{ height: 3, bgcolor: iconColor }} />
      <Box
        component="button" onClick={() => setOpen((o) => !o)}
        sx={{
          width: '100%', textAlign: 'left', border: 'none', bgcolor: 'transparent', cursor: 'pointer',
          display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2,
        }}
      >
        {open ? <ExpandLessIcon sx={{ fontSize: 20, color: GX_INK_500, mt: 0.5, flexShrink: 0 }} /> : <ExpandMoreIcon sx={{ fontSize: 20, color: GX_INK_500, mt: 0.5, flexShrink: 0 }} />}
        <GxIconChip icon={icon} color={iconColor} size={32} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: GX_INK_700 }}>{title}</Typography>
            {count != null && <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, color: GX_INK_500 }}>({count})</Typography>}
            {(tags || []).map((t) => <GxPill key={t} color={iconColor}>{t}</GxPill>)}
          </Box>
          {description && <Typography sx={{ fontSize: 12, color: GX_INK_500, mt: 0.5 }}>{description}</Typography>}
        </Box>
      </Box>
      {open && <Box sx={{ px: 2.5, pb: 2.5 }}>{children}</Box>}
    </Paper>
  )
}

// Full-shape placeholder for a section/tile whose real data source isn't connected in this
// migration (ad-platform spend, live HubSpot Contacts/Companies, a Google Sheet export) — used
// instead of a blank gap or a fabricated number, so the page's real layout stays intact while
// being honest about what's missing.
export function GxPendingPanel({ title, reason, icon }) {
  return (
    <Box sx={{
      border: `1px dashed ${GX_COLORS.amber}66`, borderRadius: '12px', p: 3, textAlign: 'center',
      bgcolor: `${GX_COLORS.amber}0a`,
    }}>
      {icon && <Box sx={{ color: GX_COLORS.amber, display: 'flex', justifyContent: 'center', mb: 1, fontSize: 22 }}>{icon}</Box>}
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: GX_INK_700 }}>{title || 'Not available yet'}</Typography>
      {reason && <Typography sx={{ fontSize: 12, color: GX_INK_500, mt: 0.5, maxWidth: 480, mx: 'auto' }}>{reason}</Typography>}
    </Box>
  )
}

// Collapsed-by-default section (e.g. "Show Sales POC table"), matching the real app's
// pattern for long ranked tables it doesn't want dumped on the page by default.
export function GxCollapsibleCard({ toggleLabel, children }) {
  const [open, setOpen] = useState(false)
  return (
    <Box>
      <Box
        component="button" onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', textAlign: 'left',
          border: `1px solid ${GX_BORDER}`, borderRadius: '10px', bgcolor: open ? `${GX_COLORS.indigo}0d` : '#fff',
          color: GX_INK_700, fontSize: 13, fontWeight: 600, cursor: 'pointer', px: 2, py: 1.25,
        }}
      >
        {open ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
        {open ? `Hide ${toggleLabel}` : `Show ${toggleLabel}`}
      </Box>
      {open && <Box sx={{ mt: 2 }}>{children}</Box>}
    </Box>
  )
}
